// Export-ready API client, shared by every app in the monorepo (ENG-194).
//
// ENG-201: the API base is resolved **per request path, at runtime**, from the
// module map in lib/config.ts — not from a compile-time VITE_ variable. The same
// static bundle has to serve same-origin from Rails today and stand alone against
// two API origins tomorrow, and one bundle per environment defeats the point.
//
// `VITE_API_BASE_URL` is still honoured as a build-time override, because the
// e2e/dev setups use it and it costs one line to keep working. Runtime config
// wins when both are present.
import { apiBaseFor, getConfig } from './config'
import type {
  PrivacySettings, ClientOverview, PrivacyRequest, PrivacyRequestKind, PrivacyRequestStatus, RespondentDetailResponse,
} from '../types'

const BUILD_TIME_API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''

// Exported (ENG-255) so callers building a plain <a href> — file downloads
// that must be a real browser navigation, not a fetch() — can absolutize
// their own paths too. A relative "/api/v1/..." href resolves against the
// SPA's own origin: nginx's try_files falls back to index.html and the
// "download" silently becomes the SPA shell instead of reaching the API.
export function apiUrl(path: string): string {
  return `${apiBaseFor(path) || BUILD_TIME_API_BASE}${path}`
}

// ENG-199 — CSRF tokens are tracked **per origin**, not in one module-global.
//
// Each Rails app mints its own CSRF token from its own session, and they rotate
// independently (after sign-in, after sign-out). Once the shell talks to two API
// origins, a single global means whichever app answered last overwrites the
// other's token, and the next write to that other app fails 422 — intermittently,
// depending on request ordering, which is a miserable thing to debug.
//
// Sending app A's token to app B must also simply not happen: it isn't a CSRF
// bypass (B would reject it), but it turns an ordering bug into a data-loss bug.
const csrfTokens = new Map<string, string>()

// Origin of a request URL. Relative paths (the same-origin Variant A case, and
// every call today) resolve against the document.
function originOf(url: string): string {
  try {
    return new URL(url, window.location.href).origin
  } catch {
    return window.location.origin
  }
}

// The <meta> token the Rails shell renders belongs to the origin that served the
// document — seed it there and nowhere else.
{
  const seeded = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  if (seeded) csrfTokens.set(window.location.origin, seeded)
}

// org_id is never stored — every call site passes it explicitly (from useOrgId(),
// which reads it straight from the URL). ENG-113: replaces the old hidden
// impersonatedOrgId module state, which went stale on refresh.
function withOrgId(path: string, orgId?: string | null): string {
  if (!orgId) return path
  // A path that already scopes itself (e.g. the Interview Library passes the
  // client id from the URL) wins — appending a second org_id would let Rack's
  // last-wins parsing silently re-scope the request.
  if (/[?&]org_id=/.test(path)) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}org_id=${orgId}`
}

// `origin` defaults to the document's, which is the right answer for every
// same-origin caller (and for the sign-in flows that call this directly).
export function setCsrfToken(token?: string | null, origin: string = window.location.origin) {
  if (token) csrfTokens.set(origin, token)
}

export function csrfTokenFor(origin: string = window.location.origin): string {
  return csrfTokens.get(origin) ?? ''
}

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.status = status
    this.body = body
  }
}

// `errors` is a string list from most endpoints, but a { field: [messages] } hash from meetings-automation.
function errorsText(errors: unknown): string | undefined {
  if (Array.isArray(errors)) return errors.join(', ')
  if (errors && typeof errors === 'object') return Object.values(errors).flat().join(', ')
  return undefined
}

async function request<T>(method: string, path: string, payload?: unknown, orgId?: string | null): Promise<T> {
  const url = apiUrl(withOrgId(path, orgId))
  const origin = originOf(url)

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (payload !== undefined) headers['Content-Type'] = 'application/json'
  const csrf = await csrfFor(path, method)
  if (method !== 'GET') headers['X-CSRF-Token'] = csrf

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body: payload === undefined ? undefined : JSON.stringify(payload),
  })

  const data = res.status === 204 ? null : await res.json().catch(() => null)
  // Adopt a rotated CSRF token if the endpoint returned one — against the origin
  // that issued it, never globally.
  if (data && typeof data === 'object' && 'csrf_token' in data) {
    setCsrfToken((data as { csrf_token?: string }).csrf_token, origin)
  }
  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && ('error' in data || 'errors' in data)
        ? (data as { error?: string; errors?: string[] }).error ??
          errorsText((data as { errors?: unknown }).errors)
        : undefined) ?? `${method} ${path} failed (${res.status})`
    throw new ApiError(res.status, message, data)
  }
  return data as T
}

// Multipart POST — file uploads (ENG-225). Deliberately does not set
// Content-Type: the browser must supply the multipart boundary itself, the
// same reason JSON requests set it explicitly and this one must not.
export async function apiPostForm<T>(path: string, form: FormData, orgId?: string | null): Promise<T> {
  const url = apiUrl(withOrgId(path, orgId))
  const origin = originOf(url)

  const res = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-CSRF-Token': await csrfFor(path, 'POST') },
    credentials: 'include',
    body: form,
  })

  const data = res.status === 204 ? null : await res.json().catch(() => null)
  if (data && typeof data === 'object' && 'csrf_token' in data) {
    setCsrfToken((data as { csrf_token?: string }).csrf_token, origin)
  }
  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && ('error' in data || 'errors' in data)
        ? (data as { error?: string; errors?: string[] }).error ??
          (data as { errors?: string[] }).errors?.join(', ')
        : undefined) ?? `POST ${path} failed (${res.status})`
    throw new ApiError(res.status, message, data)
  }
  return data as T
}

// Streams Server-Sent Events from a POST endpoint, invoking `onEvent` for each
// parsed `data:` JSON frame. Same-origin + CSRF handling as request(). Used by
// the interview turn (ENG-9 §4).
export async function streamPost<E>(
  path: string,
  payload: unknown,
  onEvent: (event: E) => void,
  orgId?: string | null,
): Promise<void> {
  const url = apiUrl(withOrgId(path, orgId))
  const csrf = await csrfFor(path, 'POST')
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-CSRF-Token': csrf,
    },
    credentials: 'include',
    body: JSON.stringify(payload ?? {}),
  })
  if (!res.ok || !res.body) {
    // Read the error body when present (e.g. ENG-183's {"error","code"} 422
    // shape) so callers can branch on `code` — same contract request() already
    // gives non-streamed callers. A stream response has no body to read twice,
    // so this consumes it instead of the SSE reader below.
    const body = res.body ? await res.json().catch(() => null) : null
    const message =
      (body && typeof body === 'object' && 'error' in body ? (body as { error?: string }).error : undefined) ??
      `stream ${path} failed (${res.status})`
    throw new ApiError(res.status, message, body)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
      if (!dataLine) continue
      const json = dataLine.slice(dataLine.indexOf(':') + 1).trim()
      if (!json) continue
      try { onEvent(JSON.parse(json) as E) } catch { /* skip malformed frame */ }
    }
  }
}

export const apiGet = <T>(path: string, orgId?: string | null) => request<T>('GET', path, undefined, orgId)
export const apiPost = <T>(path: string, payload?: unknown, orgId?: string | null) => request<T>('POST', path, payload ?? {}, orgId)
export const apiPatch = <T>(path: string, payload?: unknown, orgId?: string | null) => request<T>('PATCH', path, payload ?? {}, orgId)
export const apiPut = <T>(path: string, payload?: unknown, orgId?: string | null) => request<T>('PUT', path, payload ?? {}, orgId)
export const apiDelete = <T>(path: string, orgId?: string | null) => request<T>('DELETE', path, undefined, orgId)

// Privacy endpoints. Downloads are plain hrefs (see apiUrl).
const respondentPrivacyPath = (assessmentId: string | number, respondentId: string | number) =>
  `/api/v1/assessments/${assessmentId}/respondents/${respondentId}`

export const respondentExportUrl = (assessmentId: string | number, respondentId: string | number, orgId: string | null) =>
  apiUrl(withOrgId(`${respondentPrivacyPath(assessmentId, respondentId)}/export`, orgId))

export const clientDataExportUrl = (clientId: number) => apiUrl(`/api/v1/clients/${clientId}/data_export`)

export const updatePrivacySettings = (clientId: number, settings: PrivacySettings) =>
  apiPatch<ClientOverview>(`/api/v1/clients/${clientId}/privacy_settings`, { privacy_settings: settings })

type RespondentRef = [assessmentId: string | number, respondentId: string | number, orgId: string | null]
type RespondentResult = { respondent: Partial<RespondentDetailResponse['respondent']> }

export const eraseRespondent = (...[a, r, orgId]: RespondentRef) =>
  apiPost<RespondentResult>(`${respondentPrivacyPath(a, r)}/erase`, { confirm: true }, orgId)

export const rectifyRespondent = (
  ...[a, r, orgId, fields]: [...RespondentRef, { name: string; email: string; department: string; level?: string | null }]
) => apiPatch<RespondentResult>(respondentPrivacyPath(a, r), { respondent: fields }, orgId)

export const setRespondentRestriction = (...[a, r, orgId, restricted]: [...RespondentRef, boolean]) => {
  const path = `${respondentPrivacyPath(a, r)}/restriction`
  return restricted ? apiPost<RespondentResult>(path, {}, orgId) : apiDelete<RespondentResult>(path, orgId)
}

export const listPrivacyRequests = (...[a, r, orgId]: RespondentRef) =>
  apiGet<{ privacy_requests: PrivacyRequest[] }>(`${respondentPrivacyPath(a, r)}/privacy_requests`, orgId)
    .then((d) => d.privacy_requests)

export const createPrivacyRequest = (...[a, r, orgId, body]: [...RespondentRef, { kind: PrivacyRequestKind; note: string }]) =>
  apiPost<{ privacy_request: PrivacyRequest }>(`${respondentPrivacyPath(a, r)}/privacy_requests`, { privacy_request: body }, orgId)
    .then((d) => d.privacy_request)

export const updatePrivacyRequest = (
  ...[a, r, orgId, requestId, body]: [...RespondentRef, number, { status: PrivacyRequestStatus; note?: string }]
) =>
  apiPatch<{ privacy_request: PrivacyRequest }>(`${respondentPrivacyPath(a, r)}/privacy_requests/${requestId}`, { privacy_request: body }, orgId)
    .then((d) => d.privacy_request)

// Generic fetch helper: GET when no options given, otherwise uses options.method.
// Body should be pre-serialised JSON string when provided.
export async function api<T = unknown>(
  path: string,
  options?: { method?: string; body?: string },
  orgId?: string | null,
): Promise<T> {
  const method = options?.method ?? 'GET'
  let payload: unknown
  if (options?.body !== undefined) {
    try { payload = JSON.parse(options.body) } catch { payload = options.body }
  }
  return request<T>(method, path, payload, orgId)
}

// ENG-170 final phase, revised by ENG-202 §3 — seed a CSRF token per API origin.
//
// Same-origin, the token comes from the <meta name="csrf-token"> tag seeded at
// the top of this file, and this is a no-op. Cross-origin there is no meta tag
// and no guaranteed earlier response carrying a `csrf_token` key (the
// unauthenticated GET /api/v1/me 401 body has none, and neither does any public
// token endpoint), so the first write would otherwise send an empty token.
//
// The base is an explicit parameter rather than something this function
// resolves: apiBaseFor('/api/v1/csrf') never matches a module prefix like
// /api/v1/context_bundles, so a self-resolving version could only ever prime
// the default origin — leaving every other configured origin tokenless.
export async function primeCsrf(apiBase: string): Promise<void> {
  const origin = originOf(apiBase || window.location.href)
  if (origin === window.location.origin) return
  try {
    const res = await fetch(`${apiBase}/api/v1/csrf`, {
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
    if (!res.ok) return
    const data = (await res.json()) as { csrf_token?: string }
    setCsrfToken(data.csrf_token, origin)
  } catch {
    // Network failure or unparseable body — leave the token unset. That is the
    // honest failure: the write that needs it fails loudly rather than silently
    // posting to the wrong origin.
  }
}

const csrfPrimes = new Map<string, Promise<void>>()
let sessionPrimed: Promise<void> = Promise.resolve()

const defaultApiBase = () => getConfig().defaultApiBase || BUILD_TIME_API_BASE

// Boot, sign-in, sign-up and sign-out prime only the default API, which owns the
// session. Every other origin's token is dropped and fetched again on first use
// (csrfFor below), so a slow or absent module API (a cold Second Brain boot) never
// holds up the shell. The default origin keeps its token until the new one arrives.
//
// Other origins wait for this before priming: they share one session cookie, and
// a CSRF request fired before any cookie exists mints its own secret, which then
// overwrites the default origin's (Rails `session[:_csrf_token] ||= generate`).
export function primeSessionCsrf(): Promise<void> {
  const base = defaultApiBase()
  const keep = new Set([window.location.origin, originOf(base || window.location.href)])
  for (const origin of [...csrfTokens.keys()]) if (!keep.has(origin)) csrfTokens.delete(origin)
  csrfPrimes.clear()
  sessionPrimed = base ? primeCsrf(base) : Promise.resolve()
  return sessionPrimed
}

// A cross-origin request without a token starts one shared fetch for it. Writes
// wait for it; reads do not need a token and only start it. A failed fetch is
// forgotten, so the next request tries again.
function csrfFor(path: string, method: string): Promise<string> {
  const base = apiBaseFor(path) || BUILD_TIME_API_BASE
  const origin = originOf(base || window.location.href)
  if (csrfTokens.has(origin) || origin === window.location.origin) return Promise.resolve(csrfTokenFor(origin))
  let priming = csrfPrimes.get(origin)
  if (!priming) {
    const started: Promise<void> = sessionPrimed.then(() => primeCsrf(base)).then(() => {
      if (!csrfTokens.has(origin) && csrfPrimes.get(origin) === started) csrfPrimes.delete(origin)
    })
    priming = started
    csrfPrimes.set(origin, priming)
  }
  return method === 'GET' ? Promise.resolve('') : priming.then(() => csrfTokenFor(origin))
}
